export interface Phrase {
  id: string;
  arabic: string;
  transliteration: string;
  meaning: string;
  usage: string;
  category: 'Greeting' | 'Daily' | 'Gratitude' | 'Response' | 'Core Concepts' | 'Prayer' | 'Social' | 'Legal' | 'Emotion';
}

export const commonPhrases: Phrase[] = [
  // Greeting & Social
  {
    id: 'salam',
    arabic: 'ٱلسَّلَامُ عَلَيْكُمْ',
    transliteration: 'As-salamu alaykum',
    meaning: 'Peace be upon you',
    usage: 'The standard greeting when meeting a Muslim or entering a home.',
    category: 'Greeting'
  },
  {
    id: 'walaikum',
    arabic: 'وَعَلَيْكُمُ ٱلسَّلَامُ',
    transliteration: 'Wa alaykumu s-salam',
    meaning: 'And unto you peace',
    usage: 'The required response to "As-salamu alaykum".',
    category: 'Greeting'
  },
  {
    id: 'fi_amanillah',
    arabic: 'فِي أَمَانِ ٱللَّهِ',
    transliteration: 'Fi Amanillah',
    meaning: 'In the protection of Allah',
    usage: 'Said when saying goodbye or parting ways.',
    category: 'Greeting'
  },
  {
    id: 'barakallahu_feek',
    arabic: 'بَارَكَ ٱللَّهُ فِيكَ',
    transliteration: 'BarakAllahu Feek',
    meaning: 'May Allah bless you',
    usage: 'Said to thank someone or wish them well.',
    category: 'Social'
  },
  {
    id: 'jazakallah',
    arabic: 'جَزَاكَ ٱللَّهُ خَيْرًا',
    transliteration: 'JazakAllah Khair',
    meaning: 'May Allah reward you with goodness',
    usage: 'A better way to say "Thank you". Reply with "Wa iyyak" (And to you).',
    category: 'Response'
  },
  {
    id: 'yarhamukallah',
    arabic: 'يَرْحَمُكَ ٱللَّهُ',
    transliteration: 'YarhamukAllah',
    meaning: 'May Allah have mercy on you',
    usage: 'Said to someone who sneezes and says "Alhamdulillah".',
    category: 'Response'
  },
  {
    id: 'eid_mubarak',
    arabic: 'عِيدٌ مُبَارَكٌ',
    transliteration: 'Eid Mubarak',
    meaning: 'Blessed Festival',
    usage: 'Greeting exchanged during Eid al-Fitr and Eid al-Adha.',
    category: 'Greeting'
  },

  // Daily & Gratitude
  {
    id: 'bismillah',
    arabic: 'بِسْمِ ٱللَّهِ',
    transliteration: 'Bismillah',
    meaning: 'In the name of Allah',
    usage: 'Said before starting any action (eating, driving, working) to bless it.',
    category: 'Daily'
  },
  {
    id: 'alhamdulillah',
    arabic: 'ٱلْحَمْدُ لِلَّهِ',
    transliteration: 'Alhamdulillah',
    meaning: 'All praise is due to Allah',
    usage: 'Said to show gratitude, especially after eating, sneezing, or good news.',
    category: 'Gratitude'
  },
  {
    id: 'inshaallah',
    arabic: 'إِنْ شَاءَ ٱللَّهُ',
    transliteration: 'InshaAllah',
    meaning: 'If Allah wills',
    usage: 'Said when speaking about a future plan or event.',
    category: 'Daily'
  },
  {
    id: 'mashallah',
    arabic: 'مَا شَاءَ ٱللَّهُ',
    transliteration: 'MashAllah',
    meaning: 'What Allah has willed',
    usage: 'Said when admiring something or someone to show appreciation without envy.',
    category: 'Gratitude'
  },
  {
    id: 'subhanallah',
    arabic: 'سُبْحَانَ ٱللَّهِ',
    transliteration: 'SubhanAllah',
    meaning: 'Glory be to Allah',
    usage: 'Said when amazed by something beautiful or shocking.',
    category: 'Gratitude'
  },
  {
    id: 'astaghfirullah',
    arabic: 'أَسْتَغْفِرُ ٱللَّهَ',
    transliteration: 'Astaghfirullah',
    meaning: 'I seek forgiveness from Allah',
    usage: 'Said when one commits a mistake, sees something wrong, or feels regret.',
    category: 'Daily'
  },
  {
    id: 'la_hawla',
    arabic: 'لَا حَوْلَ وَلَا قُوَّةَ إِلَّا بِٱللَّهِ',
    transliteration: 'La Hawla Wa La Quwwata Illa Billah',
    meaning: 'There is no power nor strength except through Allah',
    usage: 'Said during times of difficulty, stress, or feeling overwhelmed.',
    category: 'Emotion'
  },
  {
    id: 'inna_lillah',
    arabic: 'إِنَّا لِلَّهِ وَإِنَّا إِلَيْهِ رَاجِعُونَ',
    transliteration: 'Inna Lillahi Wa Inna Ilayhi Raji’un',
    meaning: 'Verily we belong to Allah, and verily to Him do we return',
    usage: 'Said upon hearing of a death or a tragedy.',
    category: 'Emotion'
  },

  // Core Concepts & Prayer
  {
    id: 'shahada',
    arabic: 'ٱلشَّهَادَةُ',
    transliteration: 'Shahada',
    meaning: 'Testimony of Faith',
    usage: 'The declaration: "There is no god but Allah, and Muhammad is His Messenger".',
    category: 'Core Concepts'
  },
  {
    id: 'salah',
    arabic: 'ٱلصَّلَاةُ',
    transliteration: 'Salah',
    meaning: 'Prayer',
    usage: 'The five daily ritual prayers obligated upon every Muslim.',
    category: 'Prayer'
  },
  {
    id: 'zakat',
    arabic: 'ٱلزَّكَاةُ',
    transliteration: 'Zakat',
    meaning: 'Charity / Alms-giving',
    usage: 'Obligatory charity given to the poor (2.5% of savings).',
    category: 'Core Concepts'
  },
  {
    id: 'sawm',
    arabic: 'ٱلصَّوْمُ',
    transliteration: 'Sawm',
    meaning: 'Fasting',
    usage: 'Abstaining from food and drink from dawn to sunset (e.g., in Ramadan).',
    category: 'Core Concepts'
  },
  {
    id: 'hajj',
    arabic: 'ٱلْحَجُّ',
    transliteration: 'Hajj',
    meaning: 'Pilgrimage',
    usage: 'The pilgrimage to Mecca required once in a lifetime if able.',
    category: 'Core Concepts'
  },
  {
    id: 'taqwa',
    arabic: 'ٱلتَّقْوَىٰ',
    transliteration: 'Taqwa',
    meaning: 'God-consciousness / Piety',
    usage: 'Being aware of Allah in all actions and avoiding sin.',
    category: 'Core Concepts'
  },
  {
    id: 'iman',
    arabic: 'ٱلْإِيمَانُ',
    transliteration: 'Iman',
    meaning: 'Faith',
    usage: 'Deep belief and conviction in the heart.',
    category: 'Core Concepts'
  },
  {
    id: 'ihsan',
    arabic: 'ٱلْإِحْسَانُ',
    transliteration: 'Ihsan',
    meaning: 'Excellence / Perfection',
    usage: 'Worshipping Allah as if you see Him, or knowing He sees you.',
    category: 'Core Concepts'
  },
  {
    id: 'wudu',
    arabic: 'ٱلْوُضُوءُ',
    transliteration: 'Wudu',
    meaning: 'Ablution',
    usage: 'Ritual washing performed before prayer.',
    category: 'Prayer'
  },
  {
    id: 'ghusl',
    arabic: 'ٱلْغُسْلُ',
    transliteration: 'Ghusl',
    meaning: 'Ritual Bath',
    usage: 'Full body washing required after major impurity.',
    category: 'Prayer'
  },
  {
    id: 'adhan',
    arabic: 'ٱلْأَذَانُ',
    transliteration: 'Adhan',
    meaning: 'Call to Prayer',
    usage: 'The call announced from the mosque to signal prayer time.',
    category: 'Prayer'
  },
  {
    id: 'iqama',
    arabic: 'ٱلْإِقَامَةُ',
    transliteration: 'Iqama',
    meaning: 'Call to Start',
    usage: 'The second call to prayer, signaling the prayer is about to begin.',
    category: 'Prayer'
  },
  {
    id: 'jumuah',
    arabic: 'ٱلْجُمُعَةُ',
    transliteration: 'Jumu\'ah',
    meaning: 'Friday / Gathering',
    usage: 'The weekly congregational prayer held on Friday afternoon.',
    category: 'Prayer'
  },
  {
    id: 'dua',
    arabic: 'ٱلدُّعَاءُ',
    transliteration: 'Dua',
    meaning: 'Supplication',
    usage: 'Personal prayer asking Allah for help, forgiveness, or blessings.',
    category: 'Prayer'
  },

  // Legal & Textual
  {
    id: 'halal',
    arabic: 'حَلَال',
    transliteration: 'Halal',
    meaning: 'Permissible',
    usage: 'Actions, food, or income allowed in Islam.',
    category: 'Legal'
  },
  {
    id: 'haram',
    arabic: 'حَرَام',
    transliteration: 'Haram',
    meaning: 'Forbidden',
    usage: 'Actions, food, or income strictly prohibited in Islam.',
    category: 'Legal'
  },
  {
    id: 'sunnah',
    arabic: 'ٱلسُّنَّةُ',
    transliteration: 'Sunnah',
    meaning: 'Tradition / Way',
    usage: 'The practices, habits, and teachings of the Prophet Muhammad ﷺ.',
    category: 'Legal'
  },
  {
    id: 'hadith',
    arabic: 'ٱلْحَدِيثُ',
    transliteration: 'Hadith',
    meaning: 'Report / Saying',
    usage: 'Recorded sayings or actions of the Prophet Muhammad ﷺ.',
    category: 'Legal'
  },
  {
    id: 'ayah',
    arabic: 'آيَة',
    transliteration: 'Ayah',
    meaning: 'Sign / Verse',
    usage: 'A single verse of the Quran.',
    category: 'Legal'
  },
  {
    id: 'surah',
    arabic: 'سُورَة',
    transliteration: 'Surah',
    meaning: 'Chapter',
    usage: 'A chapter of the Quran (there are 114 Surahs).',
    category: 'Legal'
  }
];
